// useChecklistItemEditor.js

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  createEnumOptionId,
  generateEnumOptionValue,
} from 'utilities/EnumOptions';
import {
  getChecklistTemplateItemLabel,
  normalizeChecklistTemplateItems,
} from 'utilities/ChecklistTemplateItems';

/**
 * Manages checklist template item editing for the registry field editor.
 * Keys are auto-generated from labels (admins do not edit keys directly).
 * Newly added items keep key in sync with label until locked; existing items
 * keep a stable key across renames.
 */
const useChecklistItemEditor = ({ selected, selectedIndex, patchSelected }) => {
  const [itemLabelDrafts, setItemLabelDrafts] = useState({});
  const [keyAutoSyncById, setKeyAutoSyncById] = useState({});

  const hasSelected = Boolean(selected);
  const isChecklistField = selected?.type === 'checklist';
  const selectedChecklistItems = selected?.checklistItems;

  const checklistItems = useMemo(() => (
    isChecklistField ? normalizeChecklistTemplateItems(selectedChecklistItems) : []
  ), [isChecklistField, selectedChecklistItems]);

  useEffect(() => {
    if (!hasSelected || !isChecklistField) {
      setItemLabelDrafts((prev) => (prev && Object.keys(prev).length ? {} : prev));
      setKeyAutoSyncById((prev) => (prev && Object.keys(prev).length ? {} : prev));
      return;
    }

    const items = normalizeChecklistTemplateItems(selectedChecklistItems);

    setItemLabelDrafts((prev) => {
      const next = {};
      items.forEach((item) => {
        const itemId = item?.id || '';
        if (!itemId) return;
        if (prev && Object.prototype.hasOwnProperty.call(prev, itemId)) {
          next[itemId] = prev[itemId];
        }
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (prevKeys.length === nextKeys.length && nextKeys.every((key) => prev?.[key] === next[key])) {
        return prev;
      }
      if (prevKeys.length === 0 && nextKeys.length === 0) return prev || {};
      return next;
    });

    setKeyAutoSyncById((prev) => {
      const next = {};
      items.forEach((item) => {
        const itemId = item?.id || '';
        if (!itemId) return;
        next[itemId] = prev?.[itemId] === true;
      });
      const prevKeys = Object.keys(prev || {});
      const nextKeys = Object.keys(next);
      if (
        prevKeys.length === nextKeys.length
        && nextKeys.every((key) => prev?.[key] === next[key])
      ) {
        return prev;
      }
      return next;
    });
  }, [hasSelected, isChecklistField, selectedChecklistItems]);

  useEffect(() => {
    setKeyAutoSyncById((prev) => {
      if (!prev || !Object.keys(prev).length) return prev;
      let changed = false;
      const next = {};
      Object.keys(prev).forEach((itemId) => {
        if (prev[itemId] === true) changed = true;
        next[itemId] = false;
      });
      return changed ? next : prev;
    });
  }, [selectedIndex]);

  const uniqueKey = useCallback((desired, existingKeys) => {
    let candidate = desired || 'item';
    if (!existingKeys.has(candidate)) return candidate;
    let counter = 2;
    while (existingKeys.has(`${candidate}_${counter}`)) counter += 1;
    return `${candidate}_${counter}`;
  }, []);

  const handleChecklistItemAdd = useCallback(() => {
    if (selectedIndex < 0 || !selected || !isChecklistField) return;
    const existing = normalizeChecklistTemplateItems(selected.checklistItems);
    let counter = existing.length + 1;
    let label = `Item ${counter}`;
    const existingLabels = new Set(existing.map((item) => getChecklistTemplateItemLabel(item)));
    while (existingLabels.has(label)) {
      counter += 1;
      label = `Item ${counter}`;
    }
    const existingKeys = new Set(existing.map((item) => item.key));
    const key = uniqueKey(generateEnumOptionValue(label), existingKeys);
    const itemId = createEnumOptionId();
    patchSelected({
      checklistItems: [
        ...existing,
        {
          id: itemId,
          key,
          translations: { en: { label } },
          sortOrder: existing.length,
        },
      ],
    });
    setKeyAutoSyncById((prev) => ({ ...(prev || {}), [itemId]: true }));
  }, [selectedIndex, selected, patchSelected, isChecklistField, uniqueKey]);

  const handleChecklistItemRemove = useCallback((itemId) => {
    if (selectedIndex < 0 || !selected || !isChecklistField) return;
    const existing = normalizeChecklistTemplateItems(selected.checklistItems);
    patchSelected({
      checklistItems: existing
        .filter((item) => item.id !== itemId)
        .map((item, index) => ({ ...item, sortOrder: index })),
    });
  }, [selectedIndex, selected, patchSelected, isChecklistField]);

  const handleChecklistItemLabelCommit = useCallback((itemId, rawLabel) => {
    if (selectedIndex < 0 || !selected || !isChecklistField) return;
    const existing = normalizeChecklistTemplateItems(selected.checklistItems);
    const target = existing.find((item) => item.id === itemId);
    if (!target) return;

    const label = String(rawLabel || '').trim() || 'Item';
    const shouldSyncKey = keyAutoSyncById?.[itemId] === true;
    const existingKeys = new Set(
      existing.filter((item) => item.id !== itemId).map((item) => item.key),
    );
    const nextKey = shouldSyncKey
      ? uniqueKey(generateEnumOptionValue(label), existingKeys)
      : target.key;

    patchSelected({
      checklistItems: existing.map((item) => {
        if (item.id !== itemId) return item;
        return {
          ...item,
          key: nextKey,
          translations: { en: { label } },
        };
      }),
    });
  }, [
    selectedIndex,
    selected,
    patchSelected,
    isChecklistField,
    keyAutoSyncById,
    uniqueKey,
  ]);

  const handleChecklistItemMoveByDelta = useCallback((itemId, delta) => {
    if (selectedIndex < 0 || !selected || !isChecklistField) return;
    const existing = normalizeChecklistTemplateItems(selected.checklistItems);
    const fromIndex = existing.findIndex((item) => item.id === itemId);
    if (fromIndex < 0) return;
    const toIndex = fromIndex + delta;
    if (toIndex < 0 || toIndex >= existing.length) return;

    const next = [...existing];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    patchSelected({
      checklistItems: next.map((item, index) => ({ ...item, sortOrder: index })),
    });
  }, [selectedIndex, selected, patchSelected, isChecklistField]);

  return {
    checklistItems,
    itemLabelDrafts,
    setItemLabelDrafts,
    handleChecklistItemAdd,
    handleChecklistItemRemove,
    handleChecklistItemLabelCommit,
    handleChecklistItemMoveByDelta,
  };
};

export default useChecklistItemEditor;
